import {
    Button,
    Form,
    FormGroup,
    Input,
    Label,
    Modal,
    ModalBody,
    FormFeedback,
    ModalHeader, FormText,
} from "reactstrap";
import {useContext, useState} from "react";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {toast} from "react-toastify";
import Loader from "../../components/Loader";
import AuthContext from "../../context/AuthContext";
import handleUnauthorized401MutateError from "../../utils/handleUnauthorized401MutateError";
import {addCustomer, getCountry, getState} from "../../api/api";
import Select from "react-select";
import handleUnauthorized401QueryError from "../../utils/handleUnauthorized401QueryError";

const AddCustomerButton = () => {
    const { refreshToken } = useContext(AuthContext);
    const [addCustomerModal, setAddCustomerModal] = useState(false);
    const toggleAddCustomerModal = () => setAddCustomerModal(!addCustomerModal);

    const [customerName, setCustomerName] = useState("");
    const [customerEmail, setCustomerEmail] = useState("");
    const [customerImage, setCustomerImage] = useState(null);
    const [customerAddressLine1, setCustomerAddressLine1] = useState("");
    const [customerAddressLine2, setCustomerAddressLine2] = useState("");
    const [customerCountry, setCustomerCountry] = useState(null);
    const [customerState, setCustomerState] = useState(null);
    const [customerCity, setCustomerCity] = useState("");
    const [customerPhone, setCustomerPhone] = useState("");
    const [advancedOptions, setAdvancedOptions] = useState(false);
    const [countryOptions, setCountryOptions] = useState([]);
    const [stateOptions, setStateOptions] = useState([]);
    // const [cityOptions, setCityOptions] = useState([]);
    const [customerPincode, setCustomerPincode] = useState("");
    const [customerGSTIN, setCustomerGSTIN] = useState("");
    const [customerGSTINError, setCustomerGSTINError] = useState("");
    const [customerIsSEZ, setCustomerIsSEZ] = useState(false);
    const [customerSEZType, setCustomerSEZType] = useState("");
    const [customerIsRCM, setCustomerIsRCM] = useState(false);

    const queryClient = useQueryClient();

    useQuery(
        ['country'],
        () => getCountry(1, '', '', '', '', 100),
        {
            enabled: addCustomerModal,
            // staleTime: Infinity,
            onSuccess: (data) => {
                const options = data.results.map((country) => ({
                    value: country.name, label: country.name
                }));
                setCountryOptions(options);
            },
            onError: (error) => {
                handleUnauthorized401QueryError(error, refreshToken);
                if (error.response.status !== 401) {
                    toast.error(`An error occurred! ${error.response.data.detail}`);
                }
            }
        }
    );

    useQuery(
        ['state', customerCountry?.value],
        () => getState(1, '', '', customerCountry?.value, '', '', 2000),
        {
            enabled: !!customerCountry,
            // staleTime: Infinity,
            onSuccess: (data) => {
                const options = data.results.map(state => ({
                    value: state.name,
                    label: state.name
                }));
                setStateOptions(options);
                setCustomerState(null);
            },
            onError: (error) => {
                handleUnauthorized401QueryError(error, refreshToken);
                if (error.response.status !== 401) {
                    toast.error(`An error occurred! ${error.response.data.detail}`);
                }
            },
        }
    );

    // useQuery(
    //     ['city', customerState?.value],
    //     () => getCity(1, '', '', customerState?.value, '', '', 2000),
    //     {
    //         enabled: !!customerState,
    //         // staleTime: Infinity,
    //         onSuccess: (data) => {
    //             const options = data.results.map(city => ({
    //                 value: city.name,
    //                 label: city.name
    //             }));
    //             setCityOptions(options);
    //             setCustomerCity(null);
    //         },
    //         onError: (error) => {
    //             handleUnauthorized401QueryError(error, refreshToken);
    //             if (error.response.status !== 401) {
    //                 toast.error(`An error occurred! ${error.response.data.detail}`);
    //             }
    //         }
    //     }
    // );

    const addCustomerMutation = useMutation({
        mutationFn: addCustomer,
        onSuccess: (data, variables) => {
            toggleAddCustomerModal();
            queryClient.invalidateQueries(['customers']);
            setCustomerName('');
            setCustomerEmail('');
            setCustomerPhone('');
            setCustomerPincode('');
            setCustomerGSTIN('')
            setCustomerGSTINError('')
            setCustomerIsSEZ(false)
            setCustomerSEZType('')
            setCustomerIsRCM(false)
            setCustomerAddressLine1('');
            setCustomerAddressLine2('');
            setCustomerCountry(null);
            setCustomerState(null);
            setCustomerCity('');
            setAdvancedOptions(false);
            toast.success('Customer Added!');
        },
        onError: (err, variables) => {
            handleUnauthorized401MutateError(err, refreshToken, addCustomerMutation, variables);
            if (err.response.status !== 401) {
                toast.error(`An error occurred! ${err?.response?.data?.detail}`);
            }
        }
    });

    const handleAddCustomerFormSubmit = async (e) => {
        e.preventDefault();
        const formData = new FormData();
        formData.append("name", customerName);
        formData.append("email", customerEmail);
        formData.append("is_advanced_options", advancedOptions);
        formData.append("address_line1", customerAddressLine1);
        formData.append("address_line2", customerAddressLine2);
        formData.append("country", customerCountry?.value || '');
        formData.append("state", customerState?.value || '');
        // formData.append("city", customerCity?.value || '');
        formData.append("city", customerCity);
        formData.append("pincode", customerPincode);
        formData.append("gstin", customerGSTIN);
        formData.append("is_sez", customerIsSEZ);
        formData.append("sez_type", customerIsSEZ ? customerSEZType : "");
        formData.append("is_reverse_charge", customerIsRCM);
        formData.append("phone", customerPhone);
        if (customerImage) formData.append("image", customerImage);
        addCustomerMutation.mutate(formData);
    };

    return (
        <>
            <div className="fileupload btn bx-btn-primary waves-effect waves-light mb-3" onClick={toggleAddCustomerModal}>
                <span><i className="mdi mdi-plus-circle mr-1"/> Add Customer</span>
            </div>

            <Modal isOpen={addCustomerModal} toggle={toggleAddCustomerModal}>
                <ModalHeader toggle={toggleAddCustomerModal} close={
                    <button className="close" onClick={toggleAddCustomerModal}>
                        &times;
                    </button>
                }>Add Customer</ModalHeader>
                <ModalBody>
                    <Form onSubmit={handleAddCustomerFormSubmit}>
                        <FormGroup>
                            <Label for="customer-name">Name<span className="text-danger">*</span></Label>
                            <Input
                                type="text"
                                name="customer-name"
                                id="customer-name"
                                placeholder="Customer name"
                                value={customerName}
                                onChange={(e) => setCustomerName(e.target.value)}
                                // invalid={true}

                                invalid={
                                    addCustomerMutation.isError &&
                                    addCustomerMutation.error.response.data.name
                                }
                            />
                            {addCustomerMutation.isError && addCustomerMutation.error.response.data.name && (
                                <FormFeedback invalid>
                                    {addCustomerMutation.error.response.data.name}
                                </FormFeedback>
                            )}
                        </FormGroup>
                        <FormGroup>
                            <Label for="customer-email">Email<span className="text-danger">*</span></Label>
                            <Input
                                type="text"
                                name="customer-email"
                                id="customer-email"
                                placeholder="Customer Email"
                                value={customerEmail}
                                onChange={(e) => setCustomerEmail(e.target.value)}
                                invalid={
                                    addCustomerMutation.isError &&
                                    addCustomerMutation.error.response.data.email
                                }
                            />
                            {addCustomerMutation.isError && addCustomerMutation.error.response.data.email && (
                                <FormFeedback invalid>
                                    {addCustomerMutation.error.response.data.email}
                                </FormFeedback>
                            )}
                        </FormGroup>
                        <FormGroup>
                            <Label for="customer-phone">Phone </Label>
                            <Input
                                type="text"
                                name="customer-phone"
                                id="customer-phone"
                                placeholder="Customer phone"
                                value={customerPhone}
                                onChange={(e) => setCustomerPhone(e.target.value)}
                                invalid={
                                    addCustomerMutation.isError &&
                                    addCustomerMutation.error.response.data.phone
                                }
                            />
                            {addCustomerMutation.isError && addCustomerMutation.error.response.data.phone && (
                                <FormFeedback invalid>
                                    {addCustomerMutation.error.response.data.phone}
                                </FormFeedback>
                            )}
                        </FormGroup>
                        {/*{user?.membership !== "BASIC" && (*/}
                        {/*    <FormGroup className="form-group mb-3">*/}
                        {/*    <div className="custom-control custom-checkbox">*/}
                        {/*        <Input*/}
                        {/*            type="checkbox"*/}
                        {/*            className="custom-control-input"*/}
                        {/*            id="checkbox-VIP"*/}
                        {/*            checked={customerIsVIP}*/}
                        {/*            onChange={() => setCustomerIsVIP(!customerIsVIP)}*/}
                        {/*            invalid={*/}
                        {/*                addCustomerMutation.isError &&*/}
                        {/*                addCustomerMutation.error.response.data.is_vip*/}
                        {/*            }*/}
                        {/*        />*/}
                        {/*        <Label*/}
                        {/*            className="custom-control-label"*/}
                        {/*            for="checkbox-VIP"*/}
                        {/*        >*/}
                        {/*            Mark As VIP*/}
                        {/*        </Label>*/}
                        {/*    </div>*/}
                        {/*    {addCustomerMutation.isError && addCustomerMutation.error.response.data.is_vip && (*/}
                        {/*        <FormFeedback invalid>*/}
                        {/*            {addCustomerMutation.error.response.data.is_vip}*/}
                        {/*        </FormFeedback>*/}
                        {/*    )}*/}
                        {/*</FormGroup>*/}
                        {/*)}*/}
                        {/*{customerIsVIP && (*/}
                        {/*    <FormGroup className="form-group mb-3">*/}
                        {/*        <Label htmlFor="credit-limit">Credit Limit</Label>*/}
                        {/*        <Input*/}
                        {/*            type="text"*/}
                        {/*            id="credit-limit"*/}
                        {/*            name="credit-limit"*/}
                        {/*            placeholder="Credit Limit"*/}
                        {/*            value={customerCreditLimit}*/}
                        {/*            onChange={(e) => setCustomerCreditLimit(e.target.value)}*/}
                        {/*                invalid={*/}
                        {/*                    addCustomerMutation.isError &&*/}
                        {/*                    addCustomerMutation.error.response.data.phone*/}
                        {/*                }*/}
                        {/*        />*/}
                        {/*            {addCustomerMutation.isError && addCustomerMutation.error.response.data.credit_limit && (*/}
                        {/*        <FormFeedback invalid>*/}
                        {/*            {addCustomerMutation.error.response.data.credit_limit}*/}
                        {/*        </FormFeedback>*/}
                        {/*        )}*/}
                        {/*    </FormGroup>*/}
                        {/*)}*/}
                        <FormGroup className="form-group mb-3">
                            <div className="custom-control custom-checkbox">
                                <Input
                                    type="checkbox"
                                    className="custom-control-input"
                                    id="checkbox-advanced-options"
                                    checked={advancedOptions}
                                    onChange={() => setAdvancedOptions(!advancedOptions)}
                                />
                                <Label
                                    className="custom-control-label"
                                    for="checkbox-advanced-options"
                                >
                                    Advanced Options
                                </Label>
                            </div>
                        </FormGroup>
                        {advancedOptions&& (
                            <FormGroup>
                                <Label for="customer-phone">Address Line 1</Label>
                                <Input
                                    type="text"
                                    name="customer-addressline1"
                                    id="customer-addressline1"
                                    placeholder="Customer address line1"
                                    value={customerAddressLine1}
                                    onChange={(e) => setCustomerAddressLine1(e.target.value)}
                                    invalid={
                                        addCustomerMutation.isError &&
                                        addCustomerMutation.error.response.data.address_line1
                                    }
                                />
                                {addCustomerMutation.isError && addCustomerMutation.error.response.data.address_line1 && (
                                    <FormFeedback invalid>
                                        {addCustomerMutation.error.response.data.address_line1}
                                    </FormFeedback>
                                )}
                            </FormGroup>
                        )}
                        {advancedOptions&& (
                            <FormGroup>
                                <Label for="customer-phone">Address Line 2</Label>
                                <Input
                                    type="text"
                                    name="customer-addressline2"
                                    id="customer-addressline2"
                                    placeholder="Customer address line2"
                                    value={customerAddressLine2}
                                    onChange={(e) => setCustomerAddressLine2(e.target.value)}
                                    invalid={
                                        addCustomerMutation.isError &&
                                        addCustomerMutation.error.response.data.address_line2
                                    }
                                />
                                {addCustomerMutation.isError && addCustomerMutation.error.response.data.address_line2 && (
                                    <FormFeedback invalid>
                                        {addCustomerMutation.error.response.data.address_line2}
                                    </FormFeedback>
                                )}
                            </FormGroup>
                        )}
                        {advancedOptions&& (
                            <FormGroup>
                                <Label for="country">
                                    Country
                                </Label>
                                <Select
                                    options={countryOptions}
                                    placeholder = "Select Country"
                                    onChange={(option) => {
                                        setCustomerCountry(option);
                                        setCustomerState(null);
                                        setCustomerCity("");
                                    }}
                                    value={customerCountry}
                                    styles={{
                                        control: (baseStyles) => (
                                            {
                                                ...baseStyles,
                                                borderColor: "#495561",
                                                backgroundColor: "#414d59",
                                                color: "white",
                                                padding: "0.45 0.9",
                                                border: 'none',
                                                boxShadow: 'none'
                                            }
                                        ),
                                        menu: (baseStyles) => (
                                            {
                                                ...baseStyles,
                                                backgroundColor: "#414d59",
                                                color: "#a8b5c3"
                                            }
                                        ),
                                        input: (baseStyles) => (
                                            {
                                                ...baseStyles,
                                                color: "white"
                                            }
                                        ),
                                        singleValue: (baseStyles) => ({
                                            ...baseStyles,
                                            color: "white"
                                        }),
                                    }}

                                />
                            </FormGroup>
                        )}
                        {advancedOptions&& (
                            <FormGroup>
                                <Label for="state">
                                    State
                                </Label>
                                <Select
                                    options={stateOptions}
                                    placeholder = "Select State"
                                    onChange={(option) => {
                                        setCustomerState(option);
                                        setCustomerCity("");
                                    }}
                                    value={customerState}
                                    isDisabled={!customerCountry}
                                    styles={{
                                        control: (baseStyles) => (
                                            {
                                                ...baseStyles,
                                                borderColor: "#495561",
                                                backgroundColor: "#414d59",
                                                color: "white",
                                                padding: "0.45 0.9",
                                                border: 'none',
                                                boxShadow: 'none'
                                            }
                                        ),
                                        menu: (baseStyles) => (
                                            {
                                                ...baseStyles,
                                                backgroundColor: "#414d59",
                                                color: "#a8b5c3"
                                            }
                                        ),
                                        input: (baseStyles) => (
                                            {
                                                ...baseStyles,
                                                color: "white"
                                            }
                                        ),
                                        singleValue: (baseStyles) => ({
                                            ...baseStyles,
                                            color: "white"
                                        }),
                                    }}
                                />
                            </FormGroup>
                        )}
                        {/*{advancedOptions&& (*/}
                        {/*    <FormGroup>*/}
                        {/*        <Label for="city">*/}
                        {/*            City*/}
                        {/*        </Label>*/}
                        {/*        <Select*/}
                        {/*            options={cityOptions}*/}
                        {/*            placeholder = "Select City"*/}
                        {/*            onChange={(option) => setCustomerCity(option)}*/}
                        {/*            value={customerCity}*/}
                        {/*            isDisabled={!customerState}*/}
                        {/*            styles={{*/}
                        {/*                control: (baseStyles) => (*/}
                        {/*                    {*/}
                        {/*                        ...baseStyles,*/}
                        {/*                        borderColor: "#495561",*/}
                        {/*                        backgroundColor: "#414d59",*/}
                        {/*                        color: "white",*/}
                        {/*                        padding: "0.45 0.9",*/}
                        {/*                        border: 'none',*/}
                        {/*                        boxShadow: 'none'*/}
                        {/*                    }*/}
                        {/*                ),*/}
                        {/*                menu: (baseStyles) => (*/}
                        {/*                    {*/}
                        {/*                        ...baseStyles,*/}
                        {/*                        backgroundColor: "#414d59",*/}
                        {/*                        color: "#a8b5c3"*/}
                        {/*                    }*/}
                        {/*                ),*/}
                        {/*                input: (baseStyles) => (*/}
                        {/*                    {*/}
                        {/*                        ...baseStyles,*/}
                        {/*                        color: "white"*/}
                        {/*                    }*/}
                        {/*                ),*/}
                        {/*                singleValue: (baseStyles) => ({*/}
                        {/*                    ...baseStyles,*/}
                        {/*                    color: "white"*/}
                        {/*                }),*/}
                        {/*            }}*/}

                        {/*        />*/}
                        {/*    </FormGroup>*/}
                        {/*)}*/}
                        {advancedOptions&& (
                            <FormGroup>
                                <Label for="customer-city"> City </Label>
                                <Input
                                    type="text"
                                    name="customer-city"
                                    id="customer-city"
                                    placeholder="Customer City"
                                    value={customerCity}
                                    onChange={(e) => setCustomerCity(e.target.value)}
                                    invalid={
                                        addCustomerMutation.isError &&
                                        addCustomerMutation.error.response.data.city
                                    }
                                />
                                {addCustomerMutation.isError && addCustomerMutation.error.response.data.city && (
                                    <FormFeedback invalid>
                                        {addCustomerMutation.error.response.data.city}
                                    </FormFeedback>
                                )}
                            </FormGroup>
                        )}
                        {advancedOptions&& (
                            <FormGroup>
                                <Label for="customer-pincode"> Pincode </Label>
                                <Input
                                    type="text"
                                    name="customer-pincode"
                                    id="customer-pincode"
                                    placeholder="Customer pincode"
                                    value={customerPincode}
                                    onChange={(e) => setCustomerPincode(e.target.value)}
                                    invalid={
                                        addCustomerMutation.isError &&
                                        addCustomerMutation.error.response.data.pincode
                                    }
                                />
                                {addCustomerMutation.isError && addCustomerMutation.error.response.data.pincode && (
                                    <FormFeedback invalid>
                                        {addCustomerMutation.error.response.data.pincode}
                                    </FormFeedback>
                                )}
                            </FormGroup>
                        )}
                        {advancedOptions && (
                            <FormGroup>
                                <Label for="customer-pincode"> GSTIN </Label>
                                <Input
                                    type="text"
                                    name="customer-gstin"
                                    id="customer-gstin"
                                    placeholder="Customer GSTIN"
                                    value={customerGSTIN}
                                    onChange={(e) => {
                                        const val = e.target.value.toUpperCase();
                                        setCustomerGSTIN(val);
                                        setCustomerGSTINError(val && !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/.test(val)
                                            ? "Enter a valid GSTIN."
                                            : "");
                                    }}
                                    invalid={
                                        !!customerGSTINError ||
                                        (addCustomerMutation.isError &&
                                        addCustomerMutation.error.response.data.gstin)
                                    }
                                />
                                {customerGSTINError && (
                                    <FormFeedback invalid>{customerGSTINError}</FormFeedback>
                                )}
                                {!customerGSTINError && addCustomerMutation.isError && addCustomerMutation.error.response.data.gstin && (
                                    <FormFeedback invalid>
                                        {addCustomerMutation.error.response.data.gstin}
                                    </FormFeedback>
                                )}
                            </FormGroup>
                        )}
                        {advancedOptions && (
                            <FormGroup check className="mb-2">
                                <Input
                                    type="checkbox"
                                    id="customer-is-sez"
                                    checked={customerIsSEZ}
                                    onChange={() => { setCustomerIsSEZ(!customerIsSEZ); setCustomerSEZType(""); }}
                                />
                                <Label check for="customer-is-sez"> SEZ Customer</Label>
                            </FormGroup>
                        )}
                        {advancedOptions && customerIsSEZ && (
                            <FormGroup>
                                <Label for="customer-sez-type">SEZ Type</Label>
                                <Select
                                    inputId="customer-sez-type"
                                    options={[
                                        { value: "SEZ_UNIT", label: "SEZ Unit" },
                                        { value: "SEZ_DEVELOPER", label: "SEZ Developer" },
                                    ]}
                                    placeholder="Select SEZ Type"
                                    value={customerSEZType ? { value: customerSEZType, label: customerSEZType === "SEZ_UNIT" ? "SEZ Unit" : "SEZ Developer" } : null}
                                    onChange={(option) => setCustomerSEZType(option ? option.value : "")}
                                    styles={{
                                        control: (baseStyles) => ({
                                            ...baseStyles,
                                            borderColor: "#495561",
                                            backgroundColor: "#414d59",
                                            color: "white",
                                            padding: "0.45 0.9",
                                            border: 'none',
                                            boxShadow: 'none',
                                        }),
                                        menu: (baseStyles) => ({
                                            ...baseStyles,
                                            backgroundColor: "#414d59",
                                            color: "#a8b5c3",
                                        }),
                                        input: (baseStyles) => ({
                                            ...baseStyles,
                                            color: "white",
                                        }),
                                        singleValue: (baseStyles) => ({
                                            ...baseStyles,
                                            color: "white",
                                        }),
                                    }}
                                />
                                {addCustomerMutation.isError && addCustomerMutation.error?.response?.data?.sez_type && (
                                    <div className="text-danger small mt-1">
                                        {addCustomerMutation.error.response.data.sez_type}
                                    </div>
                                )}
                            </FormGroup>
                        )}
                        {advancedOptions && (
                            <FormGroup check className="mb-2">
                                <Input
                                    type="checkbox"
                                    id="customer-is-rcm"
                                    checked={customerIsRCM}
                                    onChange={() => setCustomerIsRCM(!customerIsRCM)}
                                />
                                <Label check for="customer-is-rcm"> Reverse Charge Applicable</Label>
                                <div className="small text-muted mt-1">
                                    Enable if GST on this customer's purchases is paid directly by them (Section 9(3)/(4)).
                                </div>
                            </FormGroup>
                        )}
                        <FormGroup>
                            <Label for="customer-image">Image</Label>
                            <Input
                                type="file"
                                name="customer-image"
                                id="customer-image"
                                onChange={(e) => setCustomerImage(e.target.files[0])}
                                invalid={
                                    addCustomerMutation.isError &&
                                    addCustomerMutation.error.response.data.image
                                }
                            />
                            {addCustomerMutation.isError && addCustomerMutation.error.response.data.image && (
                                <FormFeedback invalid>
                                    {addCustomerMutation.error.response.data.image}
                                </FormFeedback>
                            )}
                        </FormGroup>
                        <Button
                            color="#4d5bed"
                            className="bx-btn-primary"
                            block
                            disabled={addCustomerMutation.isLoading}
                        >
                            {addCustomerMutation.isLoading? <><Loader size={'small'}/>Adding Customer...</>: `Add Customer`}

                        </Button>
                    </Form>
                </ModalBody>
            </Modal>
        </>
    );
};

export default AddCustomerButton;